import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getMyFaturamentoAccess, cleanOAuthHash, isDomainAllowed } from '../lib/auth';

/**
 * Estado:
 *   loading          — true durante o boot inicial
 *   session          — supabase Session ou null
 *   hasAccess        — true se user logado tem app_access pra faturamento
 *   isAdmin          — true se app_access.is_admin = true (acesso ao /, ve tudo)
 *   scopes           — array de slugs permitidos (replica1, replica2, ...)
 *   accessChecked    — true depois que checamos
 *   deniedByDomain   — string email se o dominio nao for permitido (e a
 *                      sessao foi encerrada apos isso); null caso contrario.
 *                      App.jsx usa pra mostrar a tela 'Acesso Negado' por
 *                      dominio antes de qualquer outra checagem.
 *
 * NAO resetamos `loading` ou `accessChecked` em TOKEN_REFRESHED / USER_UPDATED.
 * So eventos terminais (SIGNED_IN, SIGNED_OUT) disparam o flow completo.
 */
const EMPTY_STATE = {
  loading: false,
  session: null,
  hasAccess: false,
  isAdmin: false,
  scopes: [],
  accessChecked: true,
  deniedByDomain: null,
};

export function useSession() {
  const [state, setState] = useState({
    loading: true,
    session: null,
    hasAccess: false,
    isAdmin: false,
    scopes: [],
    accessChecked: false,
    deniedByDomain: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function fullCheck(session) {
      if (!session) {
        if (!cancelled) setState({ ...EMPTY_STATE });
        return;
      }

      // ── Camada 1: dominio permitido? ──────────────────────────────────
      const email = (session.user?.email || '').toLowerCase();
      if (!isDomainAllowed(email)) {
        // Encerra a sessao no Supabase e marca o estado pra App.jsx
        // exibir a tela 'Acesso Negado por dominio'. Capturamos o email
        // antes do signOut porque depois dele session vira null.
        try { await supabase.auth.signOut(); } catch { /* noop */ }
        if (!cancelled) {
          setState({ ...EMPTY_STATE, deniedByDomain: email });
        }
        return;
      }

      // ── Camada 2: app_access / scopes ─────────────────────────────────
      const access = await getMyFaturamentoAccess();
      if (!cancelled) {
        setState({
          loading: false,
          session,
          hasAccess: access.hasAccess,
          isAdmin: access.isAdmin,
          scopes: access.scopes,
          accessChecked: true,
          deniedByDomain: null,
        });
      }
    }

    supabase.auth.getSession().then(({ data }) => {
      // getSession() aguarda o initialize do Supabase (incluindo o
      // _handleOnInit que le o hash OAuth). So agora e seguro limpar
      // o '#' cosmetico que o callback do Google deixa.
      cleanOAuthHash();
      fullCheck(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'SIGNED_OUT') {
        // Nao limpa deniedByDomain aqui — quando a propria fullCheck
        // chama signOut por dominio invalido, este event chega depois e
        // sobrescreveria nossa flag. Em vez disso, App.jsx mostra a
        // tela ate o user clicar 'Tentar com outra conta', que recarrega.
        setState(prev => ({
          ...EMPTY_STATE,
          deniedByDomain: prev.deniedByDomain,
        }));
        return;
      }
      if (event === 'SIGNED_IN') {
        fullCheck(session);
        return;
      }
      // TOKEN_REFRESHED, USER_UPDATED, etc — atualiza so a session
      setState(prev => prev.session ? { ...prev, session } : prev);
    });

    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  // Expoe o usuario logado pro feedback widget (hub.finetus.com.br). O
  // script /feedback-widget.js no index.html le window.__ETUS_USER antes
  // de submeter — sem isso, sugestoes vao como 'Anonymous'.
  useEffect(() => {
    const u = state.session?.user;
    if (u?.email) {
      const meta = u.user_metadata || {};
      const name = meta.full_name || meta.name || meta.preferred_username || null;
      window.__ETUS_USER = name ? { email: u.email, name } : { email: u.email };
    } else {
      delete window.__ETUS_USER;
    }
  }, [state.session?.user?.id, state.session?.user?.email]);

  return state;
}
